import _ from 'lodash'

import {
  convertToTargetTimeZone,
  parseAndShiftDateUTC
} from '@web/helpers/getTimeZone'

const parseDateToUTC = dateString => {
  const [month, day, year] = _.split(dateString, '-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

export const filterSensitiveAppointments = appointments => {
  // Filter out null appointments first to prevent TypeError when processing appointments
  // This was added to handle cases where the API returns null values in the appointments array
  const validAppointments = appointments?.filter(apt => apt != null) || []

  // Filter out sensitive appointments based on appointmentType and serviceType
  // This filtering logic must be identical between mobile and web to ensure consistent results
  const filteredAppointments = []
  const filteredOutAppointments = []

  _.forEach(validAppointments, item => {
    const appointmentType = _.toLower(item.appointmentType || '')
    const serviceType = _.toLower(item?.appointmentExtraData?.serviceType || '')

    // Check both appointmentType and serviceType for sensitive content
    // This comprehensive check ensures we catch sensitive appointments regardless of which field contains the data
    const shouldInclude =
      !_.includes(appointmentType, 'surgical w/pat') &&
      !_.includes(appointmentType, 'surgical pat') &&
      !_.includes(appointmentType, 'pat phone') &&
      !_.includes(appointmentType, 'sensitive') &&
      !_.includes(appointmentType, 'needs authorization only - lab') &&
      !_.includes(appointmentType, 'needs authorization only - misc') &&
      !_.includes(appointmentType, 'needs authorization only - rad') &&
      !_.includes(appointmentType, 'refer to') &&
      !_.includes(appointmentType, 'follow up') &&
      !_.includes(serviceType, 'surgical w/pat') &&
      !_.includes(serviceType, 'surgical pat') &&
      !_.includes(serviceType, 'pat phone') &&
      !_.includes(serviceType, 'sensitive') &&
      !_.includes(serviceType, 'needs authorization only - lab') &&
      !_.includes(serviceType, 'needs authorization only - misc') &&
      !_.includes(serviceType, 'needs authorization only - rad') &&
      !_.includes(serviceType, 'refer to') &&
      !_.includes(serviceType, 'follow up')

    if (shouldInclude) {
      filteredAppointments.push(item)
    } else {
      // Determine the reason for filtering
      let filterReason = 'unknown'
      if (
        _.includes(appointmentType, 'surgical') ||
        _.includes(serviceType, 'surgical')
      ) {
        filterReason = 'surgical appointment'
      } else if (
        _.includes(appointmentType, 'sensitive') ||
        _.includes(serviceType, 'sensitive')
      ) {
        filterReason = 'sensitive appointment'
      } else if (
        _.includes(appointmentType, 'authorization') ||
        _.includes(serviceType, 'authorization')
      ) {
        filterReason = 'authorization only appointment'
      } else if (
        _.includes(appointmentType, 'refer to') ||
        _.includes(serviceType, 'refer to')
      ) {
        filterReason = 'refer to appointment'
      } else if (
        _.includes(appointmentType, 'follow up') ||
        _.includes(serviceType, 'follow up')
      ) {
        filterReason = 'follow up appointment'
      } else if (
        _.includes(appointmentType, 'pat phone') ||
        _.includes(serviceType, 'pat phone')
      ) {
        filterReason = 'pat phone appointment'
      }

      filteredOutAppointments.push({
        ...item,
        filterReason
      })
    }
  })

  return filteredAppointments
}

export const filterByDate = (appointments, dependentAccount) => {
  // Only apply date filter for adolescent dependent accounts (matching mobile logic)
  // Mobile uses a "positive condition" - it checks if the account IS an adolescent dependent and then applies filtering.
  // Web uses a "negative condition" - it checks if the account is NOT an adolescent dependent and then skips filtering.
  // Both approaches are logically equivalent and achieve the same result
  if (
    !dependentAccount ||
    dependentAccount.isUser ||
    !dependentAccount.isAdolescent
  ) {
    return appointments
  }

  // Use environment variable for filter date or fallback to default date
  const CH_APPT_FILTER_DATE = !_.isEmpty(process.env.CH_APPT_FILTER_DATE)
    ? parseDateToUTC(process.env.CH_APPT_FILTER_DATE)
    : new Date('03-25-2024')
  if (_.isEmpty(appointments)) {
    return appointments
  }

  // Filter appointments to only include those after the CH_APPT_FILTER_DATE
  // This ensures adolescent accounts only see recent appointments
  const filteredAppointments = appointments.filter(item => {
    const date = new Date(item.lastUpdated || item.datetime)
    return date.getTime() > CH_APPT_FILTER_DATE.getTime()
  })

  return filteredAppointments
}

export const sortAppointmentsByDate = (appointments, isFuture = true) => {
  if (_.isEmpty(appointments)) {
    return appointments
  }
  return _.sortBy(appointments, item => {
    const date = isFuture ? item.date : item.datetime
    const time = isFuture ? item.time : '00:00:00'
    return new Date(date + 'T' + time)
  })
}

const filterAndProcessAppointments = (
  appointments,
  appointmentType,
  dependentAccount
) => {
  // Filter out null appointments first to prevent TypeError when processing appointments
  // This was added to handle cases where the API returns null values in the appointments array
  const validAppointments = appointments?.filter(apt => apt != null) || []

  // Filter out sensitive appointments based on appointmentType and serviceType
  // This filtering logic must be identical between mobile and web to ensure consistent results
  let processedAppointments = filterSensitiveAppointments(validAppointments)

  // Inject datetime for upcoming (appointmentLookup) items. The GraphQL
  // `appointmentLookup` endpoint returns `date` + `time` + `timeZone`; the
  // `appointmentPast` endpoint returns `datetime` already. Building a `datetime`
  // here keeps the sort key uniform across both hooks.
  if (appointmentType === 'appointmentLookup') {
    processedAppointments = _.map(processedAppointments, appointment => {
      const dateTimeString = `${appointment.date}T${appointment.time}`
      const conversionResult = convertToTargetTimeZone(
        dateTimeString,
        appointment.timeZone || 'UTC'
      )
      if (!conversionResult.error) {
        return { ...appointment, datetime: conversionResult.targetDate }
      }
      return appointment
    })
  }

  // Teen user has full access to all appointments, and proxy of teen can see
  // adolescent's appointments after CH_APPT_FILTER_DATE. Matches mobile logic.
  if (
    dependentAccount &&
    !dependentAccount.isUser &&
    dependentAccount.isAdolescent
  ) {
    const rawFilterDate = process.env.CH_APPT_FILTER_DATE
    const filterTimestampUTC = !_.isEmpty(rawFilterDate)
      ? parseAndShiftDateUTC(rawFilterDate)
      : Date.UTC(2024, 2, 26) // matches filterByDate's default '03-25-2024' (+1 day)
    processedAppointments = _.filter(processedAppointments, appointment => {
      const appointmentTimestampUTC = Date.parse(
        appointment.lastUpdated || appointment.datetime
      )
      return appointmentTimestampUTC > filterTimestampUTC
    })
  }

  return processedAppointments
}

export default filterAndProcessAppointments
